import { Hash, Target, Turtle, Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

type BadgeVariant =
  | 'active'
  | 'paused'
  | 'archived'
  | 'Easy'
  | 'Medium'
  | 'Hard'
  | 'brute_force'
  | 'acceptable'
  | 'optimal'
  | 'default'
  | 'premium'
  | 'topic'

const variants: Record<BadgeVariant, string> = {
  // Status badges
  active:
    'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30',
  paused:
    'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30',
  archived:
    'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/20 dark:text-slate-400 dark:border-slate-500/30',

  // Difficulty badges (title-case names from DifficultyLevel) - no icons
  Easy: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30',
  Medium:
    'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30',
  Hard: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-400 dark:border-rose-500/30',

  // Approach type badges (italic)
  brute_force:
    'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-500/20 dark:text-rose-400 dark:border-rose-500/30 italic',
  acceptable:
    'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-500/20 dark:text-amber-400 dark:border-amber-500/30 italic',
  optimal:
    'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-500/20 dark:text-emerald-400 dark:border-emerald-500/30 italic',

  // Other
  default:
    'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/20 dark:text-slate-400 dark:border-slate-500/30',
  premium:
    'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-500/20 dark:text-purple-400 dark:border-purple-500/30',
  topic:
    'bg-slate-100 text-slate-600 border-transparent dark:bg-slate-500/20 dark:text-slate-400 dark:border-transparent',
}

// Icons for specific badge variants
const variantIcons: Partial<
  Record<BadgeVariant, React.ComponentType<{ className?: string }>>
> = {
  topic: Hash,
  optimal: Zap,
  acceptable: Target,
  brute_force: Turtle,
}

interface BadgeProps {
  variant?: BadgeVariant | string
  children: React.ReactNode
  className?: string
}

export function Badge({
  variant = 'default',
  children,
  className = '',
}: BadgeProps) {
  const variantClasses = variants[variant as BadgeVariant] || variants.default
  const Icon = variantIcons[variant as BadgeVariant]

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium',
        variantClasses,
        className,
      )}
    >
      {Icon && <Icon className="mr-1 h-3 w-3" />}
      {children}
    </span>
  )
}
